import { ECGDiagnosticCode, IECGDiagnostics } from '../types'

export const ecgTypes: Record<ECGDiagnosticCode, IECGDiagnostics> = {
  '1AVB': {
    name: 'Bloqueio AV de primeiro grau',
    description:
      'Condução retardada dos átrios para os ventrículos – identificada por um intervalo PR prolongado e constante (>200 ms) com cada onda P seguida por um QRS.',
    leads: [
      'I',
      'II',
      'III',
      'aVR',
      'aVL',
      'aVF',
      'V1',
      'V2',
      'V3',
      'V4',
      'V5',
      'V6'
    ],
    insight: ['diagnostic'],
    auc: 0.76,
    emergency: 0
  },
  '2AVB': {
    name: 'Bloqueio AV de segundo grau',
    description:
      'Falha intermitente dos impulsos atriais em conduzir aos ventrículos – identificada por complexos QRS ausentes com ondas P nem sempre seguidas por QRS (padrões Mobitz I ou II).',
    leads: [
      'I',
      'II',
      'III',
      'aVR',
      'aVL',
      'aVF',
      'V1',
      'V2',
      'V3',
      'V4',
      'V5',
      'V6'
    ],
    insight: ['diagnostic'],
    auc: 0.93,
    emergency: 0.8
  },
  '3AVB': {
    name: 'Bloqueio AV de terceiro grau',
    description:
      'Ausência completa de condução atrioventricular – identificada por dissociação AV com ondas P e complexos QRS independentes em frequências diferentes.',
    leads: [
      'I',
      'II',
      'III',
      'aVR',
      'aVL',
      'aVF',
      'V1',
      'V2',
      'V3',
      'V4',
      'V5',
      'V6'
    ],
    insight: ['diagnostic'],
    auc: 0.43,
    emergency: 0.8
  },
  ABQRS: {
    name: 'QRS anormal',
    description:
      'Despolarização ventricular anormal – identificada por um complexo QRS alargado, deformado ou atípico em comparação com a morfologia normal.',
    leads: [
      'I',
      'II',
      'III',
      'aVR',
      'aVL',
      'aVF',
      'V1',
      'V2',
      'V3',
      'V4',
      'V5',
      'V6'
    ],
    insight: ['form'],
    auc: 0.88,
    emergency: 0
  },
  AFIB: {
    name: 'Fibrilação atrial',
    description:
      'Atividade elétrica atrial caótica com perda de contração atrial organizada – identificada por ondas P ausentes e intervalo RR irregularmente irregular.',
    leads: [
      'I',
      'II',
      'III',
      'aVR',
      'aVL',
      'aVF',
      'V1',
      'V2',
      'V3',
      'V4',
      'V5',
      'V6'
    ],
    insight: ['rhythm'],
    auc: 0.9,
    emergency: 0.9
  },
  AFLT: {
    name: 'Flutter atrial',
    description:
      'Ritmo de reentrada atrial rápido e organizado – identificado por ondas de flutter em dente de serra (F) e resposta ventricular regular ou regularmente irregular.',
    leads: [
      'I',
      'II',
      'III',
      'aVR',
      'aVL',
      'aVF',
      'V1',
      'V2',
      'V3',
      'V4',
      'V5',
      'V6'
    ],
    insight: ['rhythm'],
    auc: 0.96,
    emergency: 0.9
  },
  ALMI: {
    name: 'Infarto do miocárdio anterolateral',
    description:
      'Infarto do miocárdio envolvendo as paredes anterior e lateral – identificado por elevação do segmento ST, ondas Q patológicas ou alterações da onda T nas derivações I, aVL, V5 e V6.',
    leads: ['I', 'aVL', 'V5', 'V6'],
    insight: ['diagnostic'],
    auc: 0.95,
    emergency: 1
  },
  AMI: {
    name: 'Infarto do miocárdio anterior',
    description:
      'Infarto do miocárdio da parede anterior – identificado por elevação do segmento ST, ondas Q patológicas ou alterações da onda T nas derivações V1–V4.',
    leads: ['V1', 'V2', 'V3', 'V4'],
    insight: ['diagnostic'],
    auc: 0.77,
    emergency: 1
  },
  ANEUR: {
    name: 'Alterações ST-T compatíveis com aneurisma ventricular',
    description:
      'Anormalidade persistente da parede ventricular após infarto – identificada por elevação persistente do segmento ST com ondas Q profundas e evolução mínima da onda T nas mesmas derivações.',
    leads: [
      'I',
      'II',
      'III',
      'aVR',
      'aVL',
      'aVF',
      'V1',
      'V2',
      'V3',
      'V4',
      'V5',
      'V6'
    ],
    insight: ['diagnostic'],
    auc: 0.9,
    emergency: 0
  },
  ASMI: {
    name: 'Infarto do miocárdio anterosseptal',
    description:
      'Infarto do miocárdio envolvendo a região anterosseptal – identificado por elevação do segmento ST, ondas Q patológicas ou alterações da onda T nas derivações V1–V2.',
    leads: ['V1', 'V2'],
    insight: ['diagnostic'],
    auc: 0.96,
    emergency: 1
  },
  BIGU: {
    name: 'Padrão bigeminado (origem desconhecida, SV ou ventricular)',
    description:
      'Ritmo com cada batimento normal seguido por um batimento prematuro – identificado por um padrão QRS normal–prematuro repetitivo ao longo do ECG.',
    leads: [
      'I',
      'II',
      'III',
      'aVR',
      'aVL',
      'aVF',
      'V1',
      'V2',
      'V3',
      'V4',
      'V5',
      'V6'
    ],
    insight: ['rhythm'],
    auc: 0.72,
    emergency: 0
  },
  CLBBB: {
    name: 'Bloqueio completo do ramo esquerdo',
    description:
      'Bloqueio completo da condução do ramo esquerdo – identificado por duração do QRS ≥120 ms com ondas R largas e entalhadas em I, aVL, V5–V6 e ondas S profundas em V1.',
    leads: ['V1', 'V6', 'I', 'aVL'],
    insight: ['diagnostic'],
    auc: 0.99,
    emergency: 0
  },
  CRBBB: {
    name: 'Bloqueio completo do ramo direito',
    description:
      'Bloqueio completo da condução do ramo direito – identificado por duração do QRS ≥120 ms com padrão rsR′ em V1 e ondas S largas em I e V6.',
    leads: ['V1', 'V6', 'I', 'aVL'],
    insight: ['diagnostic'],
    auc: 0.98,
    emergency: 0
  },
  DIG: {
    name: 'Efeito digitálico',
    description:
      'Efeito da digoxina na repolarização ventricular – identificado por depressão do segmento ST característica descendente e "em colher" com ondas T achatadas ou invertidas.',
    leads: [
      'I',
      'II',
      'III',
      'aVR',
      'aVL',
      'aVF',
      'V1',
      'V2',
      'V3',
      'V4',
      'V5',
      'V6'
    ],
    insight: ['diagnostic', 'form'],
    auc: 0.87,
    emergency: 0
  },
  EL: {
    name: 'Distúrbio eletrolítico ou medicamentoso (antigo EDIS)',
    description:
      'Alterações no ECG devido a desequilíbrio eletrolítico ou efeitos de medicamentos – identificado por alterações difusas e inespecíficas nos segmentos ST, ondas T, intervalos ou morfologia do QRS.',
    leads: [
      'I',
      'II',
      'III',
      'aVR',
      'aVL',
      'aVF',
      'V1',
      'V2',
      'V3',
      'V4',
      'V5',
      'V6'
    ],
    insight: ['diagnostic'],
    auc: 0,
    emergency: 0.8
  },
  HVOLT: {
    name: 'Alta voltagem do QRS',
    description:
      'Aumento da amplitude elétrica ventricular – identificado por ondas R incomumente altas e/ou ondas S profundas excedendo os critérios de voltagem normais nas derivações.',
    leads: [
      'I',
      'II',
      'III',
      'aVR',
      'aVL',
      'aVF',
      'V1',
      'V2',
      'V3',
      'V4',
      'V5',
      'V6'
    ],
    insight: ['form'],
    auc: 0.74,
    emergency: 0
  },
  ILBBB: {
    name: 'Bloqueio incompleto do ramo esquerdo',
    description:
      'Atraso parcial da condução do ramo esquerdo – identificado por morfologia semelhante ao BRE com duração do QRS <120 ms.',
    leads: ['V1', 'V6', 'I', 'aVL'],
    insight: ['diagnostic'],
    auc: 0.76,
    emergency: 0
  },
  ILMI: {
    name: 'Infarto do miocárdio inferolateral',
    description:
      'Infarto do miocárdio envolvendo as paredes inferior e lateral – identificado por elevação do segmento ST, ondas Q patológicas ou alterações da onda T.',
    leads: ['I', 'aVL', 'V5', 'V6'],
    insight: ['diagnostic'],
    auc: 0.96,
    emergency: 1
  },
  IMI: {
    name: 'Infarto do miocárdio inferior',
    description:
      'Infarto do miocárdio da parede inferior – identificado por elevação do segmento ST, ondas Q patológicas ou alterações da onda T nas derivações II, III e aVF.',
    leads: ['II', 'III', 'aVF'],
    insight: ['diagnostic'],
    auc: 0.91,
    emergency: 1
  },
  INJAL: {
    name: 'Lesão subendocárdica em derivações anterolaterais',
    description:
      'Lesão isquêmica subendocárdica da região anterolateral – identificada por depressão do segmento ST e/ou inversão da onda T nas derivações I, aVL, V5 e V6.',
    leads: ['I', 'aVL', 'V5', 'V6'],
    insight: ['diagnostic'],
    auc: 0.89,
    emergency: 0.8
  },
  INJAS: {
    name: 'Lesão subendocárdica em derivações anterosseptais',
    description:
      'Lesão isquêmica subendocárdica da região anterosseptal – identificada por depressão do segmento ST e/ou inversão da onda T nas derivações V1–V2.',
    leads: ['V1', 'V2'],
    insight: ['diagnostic'],
    auc: 0,
    emergency: 0.8
  },
  INJIL: {
    name: 'Lesão subendocárdica em derivações inferolaterais',
    description:
      'Lesão isquêmica subendocárdica da região inferolateral – identificada por depressão do segmento ST e/ou inversão da onda T nas derivações I, aVL, V5 e V6.',
    leads: ['I', 'aVL', 'V5', 'V6'],
    insight: ['diagnostic'],
    auc: 0.98,
    emergency: 0.8
  },
  INJIN: {
    name: 'Lesão subendocárdica em derivações inferiores',
    description:
      'Lesão isquêmica subendocárdica da região inferior – identificada por depressão do segmento ST e/ou inversão da onda T nas derivações II, III e aVF.',
    leads: ['II', 'III', 'aVF'],
    insight: ['diagnostic'],
    auc: 0.94,
    emergency: 0.8
  },
  INJLA: {
    name: 'Lesão subendocárdica em derivações laterais',
    description:
      'Lesão isquêmica subendocárdica da região lateral – identificada por depressão do segmento ST e/ou inversão da onda T nas derivações I, aVL, V5 e V6.',
    leads: ['I', 'aVL', 'V5', 'V6'],
    insight: ['diagnostic'],
    auc: 0.54,
    emergency: 0.8
  },
  INVT: {
    name: 'Ondas T invertidas',
    description:
      'Repolarização ventricular anormal – identificada por ondas T invertidas abaixo da linha isoelétrica nas derivações afetadas.',
    leads: [
      'I',
      'II',
      'III',
      'aVR',
      'aVL',
      'aVF',
      'V1',
      'V2',
      'V3',
      'V4',
      'V5',
      'V6'
    ],
    insight: ['form'],
    auc: 0.87,
    emergency: 0.8
  },
  IPLMI: {
    name: 'Infarto do miocárdio inferoposterolateral',
    description:
      'Infarto do miocárdio envolvendo as paredes inferior, posterior e lateral – identificado por elevação do segmento ST, ondas Q patológicas ou alterações da onda T nas derivações inferiores e laterais.',
    leads: ['I', 'aVL', 'V5', 'V6'],
    insight: ['diagnostic'],
    auc: 0.92,
    emergency: 1
  },
  IPMI: {
    name: 'Infarto do miocárdio inferoposterior',
    description:
      'Infarto do miocárdio envolvendo as paredes inferior e posterior – identificado por elevação do segmento ST ou ondas Q nas derivações inferiores com elevação do ST posterior ou depressão do ST recíproca.',
    leads: ['II', 'III', 'aVF'],
    insight: ['diagnostic'],
    auc: 0.91,
    emergency: 1
  },
  IRBBB: {
    name: 'Bloqueio incompleto do ramo direito',
    description:
      'Atraso parcial da condução do ramo direito – identificado por morfologia semelhante ao BRD com duração do QRS <120 ms, tipicamente rsR′ em V1.',
    leads: ['V1', 'V6', 'I', 'aVL'],
    insight: ['diagnostic'],
    auc: 0.94,
    emergency: 0
  },
  ISCAL: {
    name: 'Isquemia em derivações anterolaterais',
    description:
      'Isquemia miocárdica na região anterolateral – identificada por depressão do segmento ST e/ou inversão da onda T nas derivações I, aVL, V5 e V6.',
    leads: ['I', 'aVL', 'V5', 'V6'],
    insight: ['diagnostic'],
    auc: 0.87,
    emergency: 0.8
  },
  ISCAN: {
    name: 'Isquemia em derivações anteriores',
    description:
      'Isquemia miocárdica na região anterior – identificada por depressão do segmento ST e/ou inversão da onda T nas derivações V1–V4.',
    leads: ['V1', 'V2', 'V3', 'V4'],
    insight: ['diagnostic'],
    auc: 0.51,
    emergency: 0.8
  },
  ISCAS: {
    name: 'Isquemia em derivações anterosseptais',
    description:
      'Isquemia miocárdica na região anterosseptal – identificada por depressão do segmento ST e/ou inversão da onda T nas derivações V1–V2.',
    leads: ['V1', 'V2'],
    insight: ['diagnostic'],
    auc: 0,
    emergency: 0.8
  },
  ISCIL: {
    name: 'Isquemia em derivações inferolaterais',
    description:
      'Isquemia miocárdica na região inferolateral – identificada por depressão do segmento ST e/ou inversão da onda T nas derivações I, aVL, V5 e V6.',
    leads: ['I', 'aVL', 'V5', 'V6'],
    insight: ['diagnostic'],
    auc: 0.87,
    emergency: 0.8
  },
  ISCIN: {
    name: 'Isquemia em derivações inferiores',
    description:
      'Isquemia miocárdica na região inferior – identificada por depressão do segmento ST e/ou inversão da onda T nas derivações II, III e aVF.',
    leads: ['II', 'III', 'aVF'],
    insight: ['diagnostic'],
    auc: 0.88,
    emergency: 0.8
  },
  ISCLA: {
    name: 'Isquemia em derivações laterais',
    description:
      'Isquemia miocárdica na região lateral – identificada por depressão do segmento ST e/ou inversão da onda T nas derivações I, aVL, V5 e V6.',
    leads: ['I', 'aVL', 'V5', 'V6'],
    insight: ['diagnostic'],
    auc: 0.82,
    emergency: 0.8
  },
  ISC_: {
    name: 'Isquemia inespecífica',
    description:
      'Isquemia miocárdica não localizada – identificada por anormalidades difusas e inespecíficas no segmento ST e/ou na onda T sem um padrão regional claro.',
    leads: [
      'I',
      'II',
      'III',
      'aVR',
      'aVL',
      'aVF',
      'V1',
      'V2',
      'V3',
      'V4',
      'V5',
      'V6'
    ],
    insight: ['diagnostic'],
    auc: 0.94,
    emergency: 0.8
  },
  IVCD: {
    name: 'Distúrbio de condução intraventricular inespecífico (bloqueio)',
    description:
      'Condução retardada dentro dos ventrículos sem padrão típico de bloqueio de ramo – identificado por alargamento do QRS com morfologia inespecífica nas derivações.',
    leads: [
      'I',
      'II',
      'III',
      'aVR',
      'aVL',
      'aVF',
      'V1',
      'V2',
      'V3',
      'V4',
      'V5',
      'V6'
    ],
    insight: ['diagnostic'],
    auc: 0.76,
    emergency: 0
  },
  LAFB: {
    name: 'Bloqueio fascicular anterior esquerdo',
    description:
      'Bloqueio do fascículo anterior esquerdo causando ativação ventricular alterada – identificado por desvio acentuado do eixo para a esquerda com padrão qR em I e aVL e padrão rS em V1–V4, com QRS normal ou levemente prolongado.',
    leads: ['V1', 'V2', 'V3', 'V4'],
    insight: ['diagnostic'],
    auc: 0.97,
    emergency: 0
  },
  'LAO/LAE': {
    name: 'Sobrecarga/aumento atrial esquerdo',
    description:
      'Aumento do átrio esquerdo devido à sobrecarga de pressão ou volume – identificado por ondas P largas e entalhadas na derivação II e uma onda P bifásica com um componente terminal negativo proeminente principalmente em V1.',
    leads: [
      'I',
      'II',
      'III',
      'aVR',
      'aVL',
      'aVF',
      'V1',
      'V2',
      'V3',
      'V4',
      'V5',
      'V6'
    ],
    insight: ['diagnostic'],
    auc: 0.76,
    emergency: 0
  },
  LMI: {
    name: 'Infarto do miocárdio lateral',
    description:
      'Infarto do miocárdio da parede lateral – identificado por elevação do segmento ST, ondas Q patológicas ou alterações da onda T nas derivações I, aVL, V5 e V6.',
    leads: ['I', 'aVL', 'V5', 'V6'],
    insight: ['diagnostic'],
    auc: 0.84,
    emergency: 1
  },
  LNGQT: {
    name: 'Intervalo QT longo',
    description:
      'Repolarização ventricular prolongada aumentando o risco de arritmia – identificado por um intervalo QT corrigido mais longo que o normal, melhor medido na derivação II.',
    leads: ['II'],
    insight: ['diagnostic', 'form'],
    auc: 0.75,
    emergency: 0.8
  },
  LOWT: {
    name: 'Ondas T de baixa amplitude',
    description:
      'Amplitude reduzida da repolarização ventricular – identificado por ondas T incomumente pequenas ou achatadas nas derivações afetadas.',
    leads: [
      'I',
      'II',
      'III',
      'aVR',
      'aVL',
      'aVF',
      'V1',
      'V2',
      'V3',
      'V4',
      'V5',
      'V6'
    ],
    insight: ['form'],
    auc: 0.87,
    emergency: 0
  },
  LPFB: {
    name: 'Bloqueio fascicular posterior esquerdo',
    description:
      'Bloqueio do fascículo posterior esquerdo alterando a ativação ventricular – identificado por desvio do eixo para a direita com padrão rS em I e aVL e padrão qR em II, III e aVF, sem outras causas de desvio do eixo para a direita.',
    leads: ['I', 'aVL', 'II', 'III', 'aVF'],
    insight: ['diagnostic'],
    auc: 0.96,
    emergency: 0
  },
  LPR: {
    name: 'Intervalo PR prolongado',
    description:
      'Condução atrioventricular retardada – identificado por um intervalo PR superior a 200 ms, melhor medido na derivação II.',
    leads: ['II'],
    insight: ['form'],
    auc: 0.72,
    emergency: 0
  },
  LVH: {
    name: 'Hipertrofia ventricular esquerda',
    description:
      'Aumento da massa muscular ventricular esquerda – identificado por aumento das voltagens do QRS atendendo aos critérios de HVE, tipicamente nas derivações V1, V5 e V6.',
    leads: ['V1', 'V5', 'V6'],
    insight: ['diagnostic'],
    auc: 0.87,
    emergency: 0
  },
  LVOLT: {
    name: 'Baixas voltagens do QRS nas derivações frontais e horizontais',
    description:
      'Amplitude elétrica ventricular reduzida – identificado por baixas voltagens do QRS nas derivações dos membros e precordiais.',
    leads: [
      'I',
      'II',
      'III',
      'aVR',
      'aVL',
      'aVF',
      'V1',
      'V2',
      'V3',
      'V4',
      'V5',
      'V6'
    ],
    insight: ['form'],
    auc: 0.77,
    emergency: 0
  },
  NDT: {
    name: 'Anormalidades T não diagnósticas',
    description:
      'Alterações menores da onda T sem significado diagnóstico específico – identificado por leve achatamento ou alterações de forma da onda T que não atendem aos critérios de isquemia ou infarto.',
    leads: [
      'I',
      'II',
      'III',
      'aVR',
      'aVL',
      'aVF',
      'V1',
      'V2',
      'V3',
      'V4',
      'V5',
      'V6'
    ],
    insight: ['diagnostic', 'form'],
    auc: 0.87,
    emergency: 0
  },
  NORM: {
    name: 'ECG normal',
    description:
      'Atividade elétrica cardíaca normal – identificado por frequência, ritmo, intervalos, eixo e morfologia de ondas normais em todas as derivações.',
    leads: [
      'I',
      'II',
      'III',
      'aVR',
      'aVL',
      'aVF',
      'V1',
      'V2',
      'V3',
      'V4',
      'V5',
      'V6'
    ],
    insight: ['diagnostic'],
    auc: 0.94,
    emergency: 0
  },
  NST_: {
    name: 'Alterações ST inespecíficas',
    description:
      'Anormalidades do segmento ST sem um padrão específico – identificado por elevação ou depressão leve do ST que não se localiza em um território coronário.',
    leads: [
      'I',
      'II',
      'III',
      'aVR',
      'aVL',
      'aVF',
      'V1',
      'V2',
      'V3',
      'V4',
      'V5',
      'V6'
    ],
    insight: ['diagnostic', 'form'],
    auc: 0.8,
    emergency: 0
  },
  NT_: {
    name: 'Alterações da onda T inespecíficas',
    description:
      'Anormalidades da onda T sem localização específica – identificado por achatamento, leve inversão ou alterações de forma da onda T que não atendem aos critérios isquêmicos.',
    leads: [
      'I',
      'II',
      'III',
      'aVR',
      'aVL',
      'aVF',
      'V1',
      'V2',
      'V3',
      'V4',
      'V5',
      'V6'
    ],
    insight: ['form'],
    auc: 0.86,
    emergency: 0
  },
  PAC: {
    name: 'Complexo atrial prematuro',
    description:
      'Despolarização atrial precoce originando fora do nó sinusal – identificado por uma onda P prematura com morfologia anormal seguida por um QRS normal ou estreito.',
    leads: [
      'I',
      'II',
      'III',
      'aVR',
      'aVL',
      'aVF',
      'V1',
      'V2',
      'V3',
      'V4',
      'V5',
      'V6'
    ],
    insight: ['form'],
    auc: 0.73,
    emergency: 0
  },
  PACE: {
    name: 'Marca-passo artificial funcionando normalmente',
    description:
      'Estimulação elétrica mantendo o ritmo cardíaco – identificado por espiculas de marca-passo precedendo a despolarização atrial e/ou ventricular com captura apropriada.',
    leads: [
      'I',
      'II',
      'III',
      'aVR',
      'aVL',
      'aVF',
      'V1',
      'V2',
      'V3',
      'V4',
      'V5',
      'V6'
    ],
    insight: ['rhythm'],
    auc: 0.96,
    emergency: 0
  },
  PMI: {
    name: 'Infarto do miocárdio posterior',
    description:
      'Infarto do miocárdio da parede posterior – identificado por elevação do segmento ST nas derivações posteriores V7–V9 ou depressão do ST recíproca com ondas R altas em V1–V3.',
    leads: ['V1', 'V2', 'V3'],
    insight: ['diagnostic'],
    auc: 0.93,
    emergency: 1
  },
  'PRC(S)': {
    name: 'Complexo(s) prematuro(s)',
    description:
      'Batimentos cardíacos ectópicos precoces – identificado por ondas P prematuras ou complexos QRS ocorrendo mais cedo do que o esperado no ritmo.',
    leads: [
      'I',
      'II',
      'III',
      'aVR',
      'aVL',
      'aVF',
      'V1',
      'V2',
      'V3',
      'V4',
      'V5',
      'V6'
    ],
    insight: ['form'],
    auc: 0.028,
    emergency: 0
  },
  PSVT: {
    name: 'Taquicardia supraventricular paroxística',
    description:
      'Ritmo supraventricular rápido de início súbito – identificado por taquicardia de complexo estreito regular com ondas P ausentes ou retrógradas.',
    leads: [
      'I',
      'II',
      'III',
      'aVR',
      'aVL',
      'aVF',
      'V1',
      'V2',
      'V3',
      'V4',
      'V5',
      'V6'
    ],
    insight: ['rhythm'],
    auc: 0.89,
    emergency: 0.9
  },
  PVC: {
    name: 'Complexo ventricular prematuro',
    description:
      'Despolarização ventricular precoce de um foco ectópico – identificado por um complexo QRS prematuro e alargado sem onda P precedente e seguido por uma pausa compensatória.',
    leads: [
      'I',
      'II',
      'III',
      'aVR',
      'aVL',
      'aVF',
      'V1',
      'V2',
      'V3',
      'V4',
      'V5',
      'V6'
    ],
    insight: ['form'],
    auc: 0.9,
    emergency: 0
  },
  QWAVE: {
    name: 'Ondas Q presentes',
    description:
      'Evidência de necrose miocárdica ou infarto prévio – identificado por ondas Q patológicas que são largas e/ou profundas nas derivações afetadas.',
    leads: [
      'I',
      'II',
      'III',
      'aVR',
      'aVL',
      'aVF',
      'V1',
      'V2',
      'V3',
      'V4',
      'V5',
      'V6'
    ],
    insight: ['form'],
    auc: 0.81,
    emergency: 0
  },
  'RAO/RAE': {
    name: 'Sobrecarga/aumento atrial direito',
    description:
      'Aumento do átrio direito devido à sobrecarga de pressão ou volume – identificado por ondas P altas e pontiagudas (P pulmonale), especialmente nas derivações II, III e aVF.',
    leads: [
      'I',
      'II',
      'III',
      'aVR',
      'aVL',
      'aVF',
      'V1',
      'V2',
      'V3',
      'V4',
      'V5',
      'V6'
    ],
    insight: ['diagnostic'],
    auc: 0.77,
    emergency: 0
  },
  RVH: {
    name: 'Hipertrofia ventricular direita',
    description:
      'Aumento da massa muscular ventricular direita – identificado por desvio do eixo para a direita, ondas R altas em V1 e ondas S profundas em V5–V6.',
    leads: ['V1', 'V5', 'V6'],
    insight: ['diagnostic'],
    auc: 0.98,
    emergency: 0
  },
  SARRH: {
    name: 'Arritmia sinusal',
    description:
      'Ritmo sinusal normal com variabilidade fisiológica da frequência – identificado por intervalos RR variáveis enquanto as ondas P mantêm morfologia sinusal normal.',
    leads: [
      'I',
      'II',
      'III',
      'aVR',
      'aVL',
      'aVF',
      'V1',
      'V2',
      'V3',
      'V4',
      'V5',
      'V6'
    ],
    insight: ['rhythm'],
    auc: 0.6,
    emergency: 0
  },
  SBRAD: {
    name: 'Bradicardia sinusal',
    description:
      'Ritmo sinusal com frequência cardíaca lenta – identificado por ondas P normais antes de cada QRS com frequência abaixo de 60 bpm.',
    leads: [
      'I',
      'II',
      'III',
      'aVR',
      'aVL',
      'aVF',
      'V1',
      'V2',
      'V3',
      'V4',
      'V5',
      'V6'
    ],
    insight: ['rhythm'],
    auc: 0.87,
    emergency: 0
  },
  SEHYP: {
    name: 'Hipertrofia septal',
    description:
      'Espessamento do septo interventricular – identificado por aumento das voltagens do QRS septal ou ondas Q anormais nas derivações septais V1–V2.',
    leads: ['V1', 'V2'],
    insight: ['diagnostic'],
    auc: 0,
    emergency: 0
  },
  SR: {
    name: 'Ritmo sinusal',
    description:
      'Ritmo normal originado do nó sinusal – identificado por ondas P positivas precedendo cada QRS com frequência regular e intervalo PR normal.',
    leads: [
      'I',
      'II',
      'III',
      'aVR',
      'aVL',
      'aVF',
      'V1',
      'V2',
      'V3',
      'V4',
      'V5',
      'V6'
    ],
    insight: ['rhythm'],
    auc: 0.77,
    emergency: 0
  },
  STACH: {
    name: 'Taquicardia sinusal',
    description:
      'Ritmo sinusal com frequência cardíaca elevada – identificado por ondas P normais antes de cada QRS com frequência acima de 100 bpm.',
    leads: [
      'I',
      'II',
      'III',
      'aVR',
      'aVL',
      'aVF',
      'V1',
      'V2',
      'V3',
      'V4',
      'V5',
      'V6'
    ],
    insight: ['rhythm'],
    auc: 0.87,
    emergency: 0
  },
  STD_: {
    name: 'Depressão ST inespecífica',
    description:
      'Depressão do segmento ST sem localização específica – identificado por depressão do ST horizontal ou descendente não confinada a um território coronário.',
    leads: [
      'I',
      'II',
      'III',
      'aVR',
      'aVL',
      'aVF',
      'V1',
      'V2',
      'V3',
      'V4',
      'V5',
      'V6'
    ],
    insight: ['form'],
    auc: 0.84,
    emergency: 0
  },
  STE_: {
    name: 'Elevação ST inespecífica',
    description:
      'Elevação do segmento ST sem um padrão isquêmico específico – identificado por elevação leve do ST que não se localiza em um território coronário ou não atende aos critérios de IAMCSST.',
    leads: [
      'I',
      'II',
      'III',
      'aVR',
      'aVL',
      'aVF',
      'V1',
      'V2',
      'V3',
      'V4',
      'V5',
      'V6'
    ],
    insight: ['form'],
    auc: 0,
    emergency: 0
  },
  SVARR: {
    name: 'Arritmia supraventricular',
    description:
      'Ritmo anormal originado acima dos ventrículos – identificado por atividade atrial irregular ou anormal com complexos QRS geralmente estreitos.',
    leads: [
      'I',
      'II',
      'III',
      'aVR',
      'aVL',
      'aVF',
      'V1',
      'V2',
      'V3',
      'V4',
      'V5',
      'V6'
    ],
    insight: ['form'],
    auc: 0.77,
    emergency: 0.9
  },
  SVTAC: {
    name: 'Taquicardia supraventricular',
    description:
      'Ritmo rápido originado acima dos ventrículos – identificado por taquicardia de complexo estreito regular, frequentemente com ondas P ausentes ou obscurecidas.',
    leads: [
      'I',
      'II',
      'III',
      'aVR',
      'aVL',
      'aVF',
      'V1',
      'V2',
      'V3',
      'V4',
      'V5',
      'V6'
    ],
    insight: ['rhythm'],
    auc: 0,
    emergency: 0.9
  },
  TAB_: {
    name: 'Anormalidade da onda T',
    description:
      'Padrão de repolarização ventricular anormal – identificado por ondas T que estão invertidas, achatadas, pontiagudas ou alteradas em relação à morfologia normal.',
    leads: [
      'I',
      'II',
      'III',
      'aVR',
      'aVL',
      'aVF',
      'V1',
      'V2',
      'V3',
      'V4',
      'V5',
      'V6'
    ],
    insight: ['form'],
    auc: 0.39,
    emergency: 0
  },
  TRIGU: {
    name: 'Padrão trigeminado (origem desconhecida, SV ou ventricular)',
    description:
      'Ritmo com dois batimentos normais seguidos por um batimento prematuro – identificado por um padrão QRS normal–normal–prematuro repetitivo no ECG.',
    leads: [
      'I',
      'II',
      'III',
      'aVR',
      'aVL',
      'aVF',
      'V1',
      'V2',
      'V3',
      'V4',
      'V5',
      'V6'
    ],
    insight: ['rhythm'],
    auc: 0.82,
    emergency: 0
  },
  VCLVH: {
    name: 'Critérios de voltagem (QRS) para hipertrofia ventricular esquerda',
    description:
      'Aumento das voltagens do QRS atendendo aos limiares de voltagem para HVE – identificado por critérios como S em V1 mais R em V5 ou V6 excedendo os limites normais.',
    leads: ['V1', 'V5', 'V6'],
    insight: ['form'],
    auc: 0.71,
    emergency: 0
  },
  WPW: {
    name: 'Síndrome de Wolff-Parkinson-White',
    description:
      'Via acessória atrioventricular causando pré-excitação – identificado por intervalo PR curto, onda delta e complexo QRS alargado.',
    leads: [
      'I',
      'II',
      'III',
      'aVR',
      'aVL',
      'aVF',
      'V1',
      'V2',
      'V3',
      'V4',
      'V5',
      'V6'
    ],
    insight: ['diagnostic'],
    auc: 0.9,
    emergency: 0
  }
}

export const xrayTypes: {
  [key: string]: { name: string; description: string }
} = {
  hernia: {
    name: 'Hérnia',
    description: 'Presença de hérnia na imagem de raio-X'
  },
  mass: {
    name: 'Massa',
    description: 'Detecção de massa na imagem de raio-X'
  },
  emphysema: {
    name: 'Enfisema',
    description: 'Presença de enfisema na imagem de raio-X'
  },
  effusion: {
    name: 'Derrame',
    description: 'Presença de derrame na imagem de raio-X'
  },
  cardiomegaly: {
    name: 'Cardiomegalia',
    description: 'Presença de cardiomegalia na imagem de raio-X'
  },
  pneumonia: {
    name: 'Pneumonia',
    description: 'Presença de pneumonia na imagem de raio-X'
  },
  nodule: {
    name: 'Nódulo',
    description: 'Presença de nódulo na imagem de raio-X'
  },
  pneumothorax: {
    name: 'Pneumotórax',
    description: 'Presença de pneumotórax na imagem de raio-X'
  },
  fibrosis: {
    name: 'Fibrose',
    description: 'Presença de fibrose na imagem de raio-X'
  },
  edema: {
    name: 'Edema',
    description: 'Presença de edema na imagem de raio-X'
  },
  atelectasis: {
    name: 'Atelectasia',
    description: 'Presença de atelectasia na imagem de raio-X'
  },
  pleural_thickening: {
    name: 'Espessamento Pleural',
    description: 'Presença de espessamento pleural na imagem de raio-X'
  },
  infiltration: {
    name: 'Infiltração',
    description: 'Presença de infiltração na imagem de raio-X'
  },
  consolidation: {
    name: 'Consolidação',
    description: 'Presença de consolidação na imagem de raio-X'
  }
}
